"use client";

import { useMemo } from "react";
import { useFetch } from "@/lib/use-fetch";
import { Badge, Card, Skeleton } from "@/components/ui";
import { BarList, Legend, StackedBar } from "@/components/charts";
import { failureHint, failureLabel, statusColor, statusLabel, type FailureReason } from "@/lib/failure-reasons";

interface Breakdown {
  byStatus: { status: string; count: number }[];
  reasons: FailureReason[];
}

// "Why messages failed" — turns a bare failure count into concrete, actionable reasons.
// Honours the same date window as the report it sits above.
export function FailureInsights({ dateFrom, dateTo }: { dateFrom?: string; dateTo?: string }) {
  const defaulted = !dateFrom;
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    // Default to a recent window so the failure aggregation never scans the account's ENTIRE
    // history (very slow for high-volume accounts). An explicit report date range overrides this.
    p.set("dateFrom", dateFrom ?? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());
    if (dateTo) p.set("dateTo", dateTo);
    return p.toString();
  }, [dateFrom, dateTo]);
  const title = defaulted ? "Why messages failed (last 30 days)" : "Why messages failed";

  const { data, loading } = useFetch<Breakdown>(`sms/failures${qs ? `?${qs}` : ""}`);

  const segments = useMemo(
    () =>
      (data?.byStatus ?? []).map((s) => ({
        label: statusLabel(s.status),
        value: s.count,
        color: statusColor(s.status)
      })),
    [data]
  );

  const total = segments.reduce((sum, s) => sum + s.value, 0);

  if (loading && !data) {
    return (
      <Card title={title}>
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
      </Card>
    );
  }

  // Nothing failed in this window — say so plainly rather than showing an empty chart.
  if (total === 0) {
    return (
      <Card title={title}>
        <p className="text-sm text-gray-600">
          No failures in this period. <Badge tone="green">All delivered or in flight</Badge>
        </p>
      </Card>
    );
  }

  const reasons = data?.reasons ?? [];
  const topReason = reasons[0];
  const hint = topReason ? failureHint(topReason) : null;

  return (
    <Card title={title} actions={<Badge tone="red">{total.toLocaleString()} failed</Badge>}>
      <StackedBar data={segments} />
      <Legend data={segments} className="mt-4" />

      {reasons.length > 0 && (
        <div className="mt-5 border-t border-gray-100 pt-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Top reasons</p>
          <BarList
            color="#ef4444"
            data={reasons.map((r) => ({ label: failureLabel(r), value: r.count }))}
          />
          {hint && (
            <p className="mt-4 rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-800 dark:bg-warning-500/10 dark:text-warning-200">
              <strong>Biggest cause:</strong> {hint}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
